/**
 * Shrinks a document photo in the browser before it is uploaded.
 *
 * The browser used to send the camera original. A full-resolution 48/50 MP phone photo weighs
 * 10–20 MB: nginx refuses anything over 10 MB before the backend sees it, and on a driver's mobile
 * connection even a smaller one takes long. The server re-encodes every document as a JPEG anyway,
 * so nothing is lost by doing the same here, at a size that still keeps a waybill legible.
 *
 * Drawing through <img> also turns the photo upright (browsers apply the EXIF orientation when
 * drawing), and lets formats the server cannot read (WebP, and HEIC where the browser can decode
 * it) arrive as JPEG. Whatever goes wrong — an old browser, a photo too large to decode on the
 * phone, a format the browser cannot read — the original file is sent, exactly as before.
 */

/** Long edge of the uploaded photo. 4096 px keeps small print readable and stays within the
 * canvas size limit of iOS Safari (16.7 MP). */
export const MAX_UPLOAD_EDGE = 4096
/** Above this a photo is re-encoded even when its size in pixels is fine. */
export const REENCODE_ABOVE_BYTES = 4 * 1024 * 1024
const JPEG_QUALITY = 0.85

/** Formats the server decodes itself; anything else is converted to JPEG when the browser can. */
const SERVER_FORMATS = ['image/jpeg', 'image/png', 'image/gif']

export interface UploadPlan {
  width: number
  height: number
}

/**
 * Decides whether a photo of this type, byte size and (upright) pixel size is re-encoded, and to
 * which size. `null` means: send the original file.
 */
export function planImageUpload(type: string, bytes: number, width: number, height: number): UploadPlan | null {
  if (!(width > 0 && height > 0)) return null
  // An animated GIF would lose its frames, and GIFs are small anyway.
  if (type === 'image/gif') return null

  const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(width, height))
  const convert = !SERVER_FORMATS.includes(type)
  if (scale === 1 && !convert && bytes <= REENCODE_ABOVE_BYTES) return null

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

/** photo.heic → photo.jpg; keeps the rest of the name. */
export function jpegFileName(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, '')
  return `${base || 'photo'}.jpg`
}

function isImageFile(file: File): boolean {
  // Some Android galleries hand over HEIC/HEIF with an empty type.
  return file.type.startsWith('image/') || (file.type === '' && /\.(heic|heif|webp|avif)$/i.test(file.name))
}

async function loadImage(file: File): Promise<{ img: HTMLImageElement; revoke: () => void }> {
  const url = URL.createObjectURL(file)
  const img = new Image()
  img.decoding = 'async'
  img.src = url
  try {
    await img.decode()
  } catch (error) {
    URL.revokeObjectURL(url)
    throw error
  }
  return { img, revoke: () => URL.revokeObjectURL(url) }
}

export async function prepareUploadImage(file: File): Promise<File> {
  if (!isImageFile(file)) return file
  try {
    const { img, revoke } = await loadImage(file)
    try {
      // naturalWidth/Height are already upright: browsers apply the EXIF orientation.
      const plan = planImageUpload(file.type, file.size, img.naturalWidth, img.naturalHeight)
      if (!plan) return file

      const canvas = document.createElement('canvas')
      canvas.width = plan.width
      canvas.height = plan.height
      const ctx = canvas.getContext('2d')
      if (!ctx) return file
      // JPEG has no transparency: a screenshot with alpha would otherwise turn black behind.
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, plan.width, plan.height)
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, 0, 0, plan.width, plan.height)

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
      if (!blob || blob.size === 0) return file
      // Re-encoding only for bytes, and it did not help: keep the original.
      const resized = plan.width !== img.naturalWidth || plan.height !== img.naturalHeight
      if (!resized && SERVER_FORMATS.includes(file.type) && blob.size >= file.size) return file

      return new File([blob], jpegFileName(file.name), { type: 'image/jpeg', lastModified: file.lastModified })
    } finally {
      revoke()
    }
  } catch {
    return file
  }
}
